/**
 * The one "is this a real price" rule. A card's point value is real only when
 * it is a finite number above 0. Null and undefined mean the backend has no
 * price; 0 is never a price either (a stored legacy referencePrice, or a
 * failed lookup that came back as 0), so all three read as "no price".
 */
export const hasRealPrice = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

/** The first real price among the candidates, or null when none is. */
export const firstRealPrice = (...values: unknown[]): number | null => {
  const found = values.find(hasRealPrice);
  return found === undefined ? null : found;
};

/** Sum of the real prices; a no-price entry contributes nothing. */
export const sumRealPrices = (values: unknown[]): number =>
  values.reduce<number>((sum, value) => (hasRealPrice(value) ? sum + value : sum), 0);

/** calculate-price's codes for a card it cannot put a number on. */
export const NO_PRICE_CODES = ['NO_PRICE_AVAILABLE', 'PRICE_LOOKUP_FAILED'] as const;
export type NoPriceCode = (typeof NO_PRICE_CODES)[number];

/**
 * Whether a rejected request says "this card has no number": the single-card
 * calculate-price 400, and the initiate / add-cards refusals. RTK Query puts
 * the response body on `data`; the body is { status, code, message }.
 */
export const isNoPriceError = (error: unknown): boolean => {
  const code = (error as { data?: { code?: unknown } } | null | undefined)?.data?.code;
  return typeof code === 'string' && (NO_PRICE_CODES as readonly string[]).includes(code);
};

/**
 * After a staged row's re-price fails: should the row lose its number?
 * Yes when the backend says the card has no price, and yes for any other
 * failure once the printing, condition, language or finish changed, because
 * the old number then describes a different card. Only an unchanged row keeps
 * what it had.
 */
export const dropsPriceOnRepriceError = (error: unknown, changedFields: object): boolean =>
  isNoPriceError(error) || Object.keys(changedFields).length > 0;

/** A list row's price as a surface should read it. */
export interface FilledPrice {
  /** The real price, or null. */
  price: number | null;
  /** The list sent no price and the calculate-price fill has not answered yet. */
  pending: boolean;
  /** Answered, and there is no price: render "No price" and block staging. */
  hasNoPrice: boolean;
}

/**
 * Reads a want-list row's price from the list's own displayPrice, else from the
 * calculate-price fill. A key holding null in the fill map means "answered, no
 * price"; a missing key means the answer is still on its way.
 */
export const readFilledPrice = (
  displayPrice: number | null | undefined,
  fill: Record<number, number | null>,
  id: number,
): FilledPrice => {
  const price = firstRealPrice(displayPrice, fill[id]);
  const pending = price === null && displayPrice == null && !(id in fill);
  return { price, pending, hasNoPrice: price === null && !pending };
};

/** The fill map entries for one calculate-price answer: a non-real slot is stored as null. */
export const fillFromSlots = (
  slots: Array<{ wantListId: number; displayPrice: number | null }>,
): Record<number, number | null> => {
  const fill: Record<number, number | null> = {};
  slots.forEach((slot) => { fill[slot.wantListId] = hasRealPrice(slot.displayPrice) ? slot.displayPrice : null; });
  return fill;
};

/**
 * The fill map entries when the request failed. Only the backend's own "no
 * price" refusal (a lone no-price card is a coded 400) answers the question:
 * those rows are stored as null. Any other failure (network, 429, 500) says
 * nothing about the cards, so no keys are written and the rows stay pending:
 * the price slot is empty and the card can still be staged, where the staged
 * row prices itself and the Create / Send gate blocks a card with no price.
 */
export const fillFromError = (error: unknown, ids: number[]): Record<number, null> => {
  const fill: Record<number, null> = {};
  if (isNoPriceError(error)) ids.forEach((id) => { fill[id] = null; });
  return fill;
};
